import { useMemo } from 'react'

import { runningDataService, useDataState } from '~/data/data-context'
import { entriesOn, summaryOf, type Entry, type Goal, type NutritionData } from '~/data/nutrition'

/**
 * The diary, read off the mirror the change feed fills — the diary page, the metrics row, the
 * day headings, the chips on a workout and a set — so an entry logged in one place is on the
 * others in the same breath. Null until the feed has been pulled at least once, so nothing
 * downstream claims an empty day it has not read.
 *
 * Every entry the account has is held, so a day is summed here rather than asked for, and the
 * shape of the whole diary falls out of the same rows the day's list is drawn from. The
 * expenditure is not held at all: it is worked out from these days, the weigh-ins and the
 * stats wherever it is read — see `expenditure.ts`.
 *
 * A write lands here first and is sent after — see the write helpers at the foot of this file.
 * They are plain functions rather than hooks because they are called from inside handlers, so
 * they reach the mirror through the running service rather than through the context.
 */

/** The goals in the order the diary reads them: oldest first, ties broken by when they were set. */
function byWhenInForce(a: Goal, b: Goal): number {
  return a.effectiveFrom === b.effectiveFrom
    ? a.createdAt - b.createdAt
    : a.effectiveFrom < b.effectiveFrom
      ? -1
      : 1
}

/** The diary as it stands: null while the feed has not landed, then everything logged. */
export function useNutrition(): NutritionData | null {
  const { entries, days, goals, settings, synced } = useDataState()
  return useMemo(() => {
    if (!synced || entries === null || days === null || goals === null) {
      return null
    }
    const byDay = new Map<string, Entry[]>()
    for (const entry of entries) {
      byDay.set(entry.day, [...(byDay.get(entry.day) ?? []), entry])
    }
    return {
      days: [...byDay.entries()]
        .sort((a, b) => (a[0] < b[0] ? -1 : 1))
        .map(([day, onIt]) => summaryOf(day, onIt)),
      incompleteDays: days.filter((day) => day.incomplete).map((day) => day.day),
      goals: [...goals].sort(byWhenInForce),
      weeklyCheckinOn: settings?.weeklyCheckinOn ?? null,
    }
  }, [entries, days, goals, settings, synced])
}

/**
 * A day's entries: null until the feed has landed. Pass null for no day at all — a page whose
 * day is not yet known — and nothing is read.
 */
export function useDayEntries(day: string | null): readonly Entry[] | null {
  const { entries, synced } = useDataState()
  return useMemo(
    () => (!synced || entries === null || day === null ? null : entriesOn(entries, day)),
    [entries, day, synced],
  )
}

/**
 * The amount a food was last logged in, as far as the mirror says. Null where nothing held
 * has it.
 */
export function lastLoggedAmountHeld(key: string): Entry['amount'] | null {
  const entries = runningDataService()?.getState().entries ?? []
  let latest: Entry | null = null
  for (const entry of entries) {
    if (
      entry.food !== null &&
      (entry.food.id ?? `library:${entry.food.libraryCode ?? entry.food.name}`) === key &&
      (latest === null || entry.updatedAt > latest.updatedAt)
    ) {
      latest = entry
    }
  }
  return latest?.amount ?? null
}

/** Asks the feed for whatever has moved, keeping what is held until the answer lands. */
export function reloadNutrition(): void {
  void runningDataService()?.refresh()
}

/** A saved entry takes its place, whether it is new or changed. */
export function keepEntry(saved: Entry): void {
  void runningDataService()?.keep('entry', saved.id, saved)
}

/**
 * Entries logged: they stand on their days at once and are sent behind the press — the
 * nutrition spec, Logging an entry. The service carries the send, so it outlives whatever page
 * the key that logged them was on.
 */
export function logEntries(entries: readonly Entry[]): void {
  void runningDataService()?.logEntries(entries)
}

/**
 * Waits for whatever logging is still on its way. What writes an entry straight to the server
 * waits on this first: an undo that took away a row the server had not been sent yet would
 * find nothing there, and the queued log would then put it back.
 */
export async function logsSent(): Promise<void> {
  await runningDataService()?.drain()
}

export function dropEntries(ids: readonly string[]): void {
  void runningDataService()?.keepAll('entry', [], ids)
}

/**
 * Several entries laid in at once, and any taken away with them — one change to the screen
 * rather than one per entry. What one press changed comes back the way it went: together.
 */
export function keepEntries(saved: readonly Entry[], gone: readonly string[] = []): void {
  void runningDataService()?.keepAll(
    'entry',
    saved.map((entry) => ({ id: entry.id, row: entry })),
    gone,
  )
}

export function keepDayIncomplete(day: string, incomplete: boolean): void {
  const service = runningDataService()
  if (incomplete) {
    void service?.keep('day', day, { day, incomplete: true })
  } else {
    void service?.drop('day', day)
  }
}

/**
 * A goal written, kept. A goal set joins the history, which stays oldest first; a goal that
 * only grew a set of targets — what a weekly check-in taken does — replaces the one held under
 * the same id, so no goal is added by it.
 */
export function keepGoal(saved: Goal): void {
  void runningDataService()?.keep('goal', saved.id, saved)
}

/**
 * One goal off the mirror — what reopening does to the goal it undoes, which the server
 * destroys outright rather than keeping as one gone by.
 */
export function dropOneGoal(id: string): void {
  void runningDataService()?.drop('goal', id)
}

export function dropGoal(): void {
  const service = runningDataService()
  if (service === null) {
    return
  }
  for (const goal of service.getState().goals ?? []) {
    void service.drop('goal', goal.id)
  }
  void service.keep('settings', 'me', {
    ...service.getState().settings,
    weeklyCheckinOn: null,
  })
}

export function keepWeeklyCheckinOn(day: string): void {
  const service = runningDataService()
  if (service === null) {
    return
  }
  void service.keep('settings', 'me', {
    ...service.getState().settings,
    weeklyCheckinOn: day,
  })
}
